import { useEffect, useState } from 'react';
import { ChevronLeft, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useChatStore } from '@/stores/chat-store';
import { AiConversationList } from './ai-conversation-list';
import { AiConversationThread } from './ai-conversation-thread';

type View = 'list' | 'chat';

/**
 * Sidebar AI chat panel with per-user conversation threads. The panel header
 * ("VTAK AI") is rendered by the panel container; this adds one slim toolbar
 * row that adapts to the active view (list ↔ thread).
 */
export function AiChatPanel(): React.JSX.Element {
  const serviceAvailable = useChatStore((s) => s.serviceAvailable);
  const [view, setView] = useState<View>('list');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!loaded) {
      setLoaded(true);
      useChatStore.setState({ serviceAvailable: true });
      useChatStore.getState().loadAiConversations();
    }
  }, [loaded]);

  function openConversation(id: string): void {
    useChatStore.getState().loadAiMessages(id);
    setView('chat');
  }

  async function handleNew(): Promise<void> {
    await useChatStore.getState().createAiConversation();
    setView('chat');
  }

  if (!serviceAvailable) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 p-4 text-center">
        <p className="text-sm text-muted-foreground">VTAK AI is unavailable</p>
        <p className="text-xs text-muted-foreground/60">Database connection required. Check server logs.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <PanelToolbar
        view={view}
        onBack={() => setView('list')}
        onNew={handleNew}
      />
      {view === 'list' ? (
        <AiConversationList onSelect={openConversation} onNew={handleNew} />
      ) : (
        <AiConversationThread />
      )}
    </div>
  );
}

/** Slim toolbar under the panel header: back + title in a thread, New in the list. */
function PanelToolbar({
  view,
  onBack,
  onNew,
}: {
  view: View;
  onBack: () => void;
  onNew: () => void;
}): React.JSX.Element {
  const conversations = useChatStore((s) => s.aiConversations);
  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const currentTitle =
    conversations.find((c) => c.id === activeConversationId)?.title ?? 'New conversation';

  return (
    <div className="flex items-center gap-2 border-b border-echo-border px-2 py-2">
      {view === 'chat' ? (
        <>
          <Button size="icon" variant="ghost" onClick={onBack} className="h-7 w-7 shrink-0">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <p className="min-w-0 flex-1 truncate text-xs font-medium text-muted-foreground">
            {currentTitle}
          </p>
        </>
      ) : (
        <p className="min-w-0 flex-1 px-2 font-chakra text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          Conversations
        </p>
      )}
      <Button
        size="icon"
        variant="ghost"
        onClick={onNew}
        className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
        title="New conversation"
      >
        <Plus className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
